<template>
  <BaseModal
    v-model:open="isOpen"
    dialog-panel-class="w-full xl:max-w-7xl bg-gray-50 subscription-modal"
    dialog-wrapper-class="flex min-h-full lg:items-center items-start justify-center"
  >
    <form
      id="subscription-form"
      class="form"
      novalidate
      @submit.prevent="onSubmit()"
    >
      <div
        class="form__content"
      >
        <div class="grid xl:grid-cols-8 h-full xl:min-h-[734px]">
          <div class="col-span-full xl:col-span-5 xl:p-12 p-4">
            <div class="hidden xl:block">
              <h3
                class="text-2xl md:text-3xl font-medium text-balance mb-4 mt-6 text-left"
              >
                {{ form?.title || '-' }}
              </h3>

              <div v-if="!!form?.subtitle" class="mb-6">
                <p class="text-sm">
                  {{ form?.subtitle || '-' }}
                </p>
              </div>
            </div>

            <LeadModalSubscriptionDetails class="xl:mb-8 mb-0" />

            <template v-if="!isLeadSent">
              <div class="block xl:hidden">
                <h3
                  class="text-xl md:text-3xl font-medium text-balance mb-4 mt-6"
                >
                  {{ form?.title || '-' }}
                </h3>

                <div v-if="!!form?.subtitle" class="mb-6">
                  <p class="text-sm">
                    {{ form?.subtitle || '-' }}
                  </p>
                </div>
              </div>

              <fieldset class="grid xl:grid-cols-2 gap-x-4 gap-y-2 mb-2">
                <div>
                  <BaseInput
                    id="firstName"
                    v-model="firstName"
                    v-bind="firstNameProps"
                    :has-error="Boolean(errors.firstName)"
                    maxlength="50"
                    :label="t('first_name')"
                    autocomplete="name"
                    size="xl"
                  />
                  <BaseErrorBag
                    :has-error="Boolean(errors.firstName)"
                    class="mt-2"
                  >
                    {{ errors.firstName }}
                  </BaseErrorBag>
                </div>

                <div>
                  <BaseInputPhone
                    id="phoneNumber"
                    v-model="phoneNumber"
                    v-bind="phoneNumberProps"
                    :has-error="Boolean(errors.phoneNumber)"
                    maxlength="25"
                    :prefix-label="t('phone_prefix')"
                    :label="t('phone_number')"
                    autocomplete="tel-extension"
                    size="xl"
                  />
                  <BaseErrorBag
                    :has-error="Boolean(errors.phoneNumber)"
                    class="mt-2"
                  >
                    {{ errors.phoneNumber }}
                  </BaseErrorBag>
                </div>
              </fieldset>
              <fieldset>
                <div>
                  <BaseInput
                    id="email"
                    v-model="email"
                    v-bind="emailProps"
                    :has-error="Boolean(errors.email)"
                    maxlength="50"
                    :label="t('email')"
                    autocomplete="email"
                    size="xl"
                  />
                  <BaseErrorBag
                    :has-error="Boolean(errors.email)"
                    class="mt-2"
                  >
                    {{ errors.email }}
                  </BaseErrorBag>
                </div>
              </fieldset>

              <div class="mt-2">
                <BaseButton
                  type="submit"
                  class="w-full"
                  variant="primary"
                  size="xl"
                  data-track="lead-subscription-cta"
                >
                  {{ form?.cta_button_text || $t('continue') }}
                </BaseButton>
              </div>

              <fieldset class="mt-2">
                <div class="flex gap-4 items-center">
                  <BaseCheckbox
                    id="terms"
                    v-model="termsAndCons"
                    v-bind="termsAndConsProps"
                    :has-error="Boolean(errors.termsAndCons)"
                  />
                  <label for="terms" class="semibold">
                    {{ termsAndConsChunks?.rest?.[0] || '-' }} <NuxtLink
                      class="underline"
                      :to="form?.terms_and_cons_url || ''"
                      target="_blank"
                    >{{ termsAndConsChunks?.interpolated || '-' }}</NuxtLink>{{ termsAndConsChunks?.rest?.[1] }}
                  </label>
                </div>
                <BaseErrorBag
                  :has-error="Boolean(errors.termsAndCons)"
                  class="mt-1"
                >
                  {{ errors.termsAndCons }}
                </BaseErrorBag>
              </fieldset>
            </template>
          </div>

          <div class="xl:col-span-3 bg-white xl:w-full max-h-72 xl:max-h-full xl:h-full relative hidden xl:block">
            <div class="bg-black/25 absolute top-0 left-0 w-full h-full" />
            <img
              loading="eager"
              :src="form?.image_desktop?.meta?.download_url"
              :alt="(form?.title as string)"
              class="h-full w-full object-cover"
            >
          </div>
        </div>
      </div>
    </form>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch, inject, computed } from 'vue';
import BaseButton from '~/components/BaseButton.vue';
import BaseInput from '~/components/forms/BaseInput.vue';
import BaseCheckbox from '~/components/forms/BaseCheckbox.vue';
import BaseInputPhone from '~/components/forms/BaseInputPhone.vue';
// import LeadModalSuccess from '~/components/modals/LeadModalSuccess.vue';
import { useForm } from 'vee-validate';
import { z } from 'zod';
import { toTypedSchema } from '@vee-validate/zod';
import { PHONE_REGEX, ALPHANUMERIC_REGEX, TRACKING_EVENTS } from '~/lib/constants';
import LeadModalSubscriptionDetails from './LeadModalSubscriptionDetails.vue';
import BaseErrorBag from '~/components/forms/BaseErrorBag.vue';
import { useApi, useGTM } from '~/composables';
import { useCartStore } from '~/stores/cartStore';
import { storeToRefs } from 'pinia';
import { isoDateToApiFormat } from '~/lib/transformers';
import type { Analytics, FormItem } from '~/types';
import { extractTextInterpolation, isExpiredDate } from '~/stores/helpers';
import { useI18n, useRouter } from '#imports';
import BaseModal from '~/components/modals/BaseModal.vue';

const { t, locale } = useI18n({
  messages: {
    es: {
      first_name: 'Nombre completo',
      phone_number: 'Núm. de teléfono',
      phone_prefix: 'Prefijo',
      email: 'Dirección de email',
      first_name__error_r: 'Debes introducir tu nombre',
      first_name__error_m: 'Debes introducir un nombre válido',
      email__error_r: 'Debes introducir un email',
      email__error_m: 'Debes introducir un email válido',
      phone_number__error_r: 'Debes introducir un número de teléfono',
      phone_number__error_m: 'Debes introducir un número de teléfono válido',
      terms_and_cons__error: 'Debes aceptar los términos y condiciones',
    },
    en: {
      first_name: 'Full Name',
      phone_number: 'Phone Number',
      phone_prefix: 'Code',
      email: 'Email Address',
      first_name__error_r: 'You must enter your name',
      first_name__error_m: 'You must enter a valid name',
      email__error_r: 'You must enter an email',
      email__error_m: 'You must enter a valid email',
      phone_number__error_r: 'You must enter a phone number',
      phone_number__error_m: 'You must enter a valid phone number',
      terms_and_cons__error: 'You must accept the terms and conditions',
    },
    pt: {
      first_name: 'Nome completo',
      phone_number: 'Número de telefone',
      phone_prefix: 'Prefixo',
      email: 'Endereço de email',
      first_name__error_r: 'Deve introduzir o seu nome',
      first_name__error_m: 'Deve introduzir um nome válido',
      email__error_r: 'Deve introduzir um email',
      email__error_m: 'Deve introduzir um email válido',
      phone_number__error_r: 'Deve introduzir um número de telefone',
      phone_number__error_m: 'Deve introduzir um número de telefone válido',
      terms_and_cons__error: 'Deve aceitar os termos e condições',
    },
  },
});

const webSettings = inject('settings') as Record<string, any>;
const form: FormItem | undefined = webSettings.forms.find((b: FormItem) => {
  return b?.name === 'subscription_lead_form' && b.locale === locale.value;
});
const termsAndConsChunks = computed(() => extractTextInterpolation(form?.terms_and_cons_text || ''));

const validationSchema = toTypedSchema(
  z.object({
    firstName: z.string({ required_error: t('first_name__error_r') }).regex(new RegExp(ALPHANUMERIC_REGEX), { message: t('first_name__error_m') }),
    phoneNumber: z.string({ required_error: t('phone_number__error_r') }).regex(new RegExp(PHONE_REGEX), { message: t('phone_number__error_m') }),
    email: z.string({ required_error: t('email__error_r') }).email({ message: t('email__error_m') }),
    termsAndCons: z.boolean({ required_error: t('terms_and_cons__error') }).refine(val => val === true, {
      message: t('terms_and_cons__error'),
    }),
  }),
);

const { errors, defineField, handleSubmit, resetForm } = useForm({ validationSchema });

const isOpen = ref(false);
const isLeadSent = ref(false);
const [firstName, firstNameProps] = defineField('firstName');
const [phoneNumber, phoneNumberProps] = defineField('phoneNumber');
const [email, emailProps] = defineField('email');
const [termsAndCons, termsAndConsProps] = defineField('termsAndCons');
const emit = defineEmits(['close']);
const { sendSubscriptionLead } = useApi();
const { selectedBooking, selectedCar } = storeToRefs(useCartStore());
const { triggerEvent, triggerConversion } = useGTM();
const router = useRouter();

function closeModal () {
  isOpen.value = false;
  emit('close', { isLeadSent: isLeadSent.value });
}

function openModal () {
  isOpen.value = true;
}

// const gtag = useGtag();

const onSubmit = handleSubmit(
  async (fields) => {
    const booking = selectedBooking.value;
    try {
      const payload = {
        start: isExpiredDate(isoDateToApiFormat(booking?.start as string))
          ? isoDateToApiFormat(new Date().toISOString())
          : isoDateToApiFormat(booking?.start as string),
        duration: String(booking?.monthsLong),
        vehicle_id: String(selectedCar.value?.id),
        station_data: {
          id: String(booking?.station?.id),
          type: booking?.station?.element_type as string,
        },
        km: 1,
        customer: {
          fullname: fields.firstName,
          email: fields.email,
          phone: fields.phoneNumber,
          accepted_terms_and_cons: fields.termsAndCons,
        },
      };
      await sendSubscriptionLead(payload);

      const analytics = webSettings.analytics as Analytics;
      triggerConversion(analytics.g_ads, analytics.g_ads_conversion_id);
      triggerEvent(TRACKING_EVENTS.FORM_SUBSCRIPTION_SUBMIT, { ...payload, customer: '*****' });

      router.push('/thank-you');

      isLeadSent.value = true;
    } catch (error: any) {
      console.error('[SubscriptionLeadModal] error: ', error);
    }
  },
);

watch(isOpen, (newVal) => {
  if (newVal === false) {
    resetForm();

    setTimeout(() => {
      isLeadSent.value = false;
    }, 300);
  }
});

defineExpose({ openModal, closeModal, isOpen });
</script>

<style scoped>
.form {
  @apply pt-10 xl:pt-0;
  .form__content {
    @apply  p-0;
  }
}
</style>
